import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronDown, Lock } from "lucide-react";
import { toast } from "sonner";

export const CATEGORY_META = [
  { key: "work_files", label: "Work files", cap: "Files you edit — code, docs, spreadsheets (titles and changes)", never: "File contents with secrets, keys, or IDs" },
  { key: "git_commits", label: "Git commits", cap: "Commit messages and PR descriptions", never: "Source diffs, tokens, or credentials in commits" },
  { key: "terminal_commands", label: "Terminal commands", cap: "Significant commands — deploys, builds, migrations", never: "Commands containing passwords, keys, or tokens" },
  { key: "work_sessions", label: "Work sessions", cap: "Session start/end timestamps and duration", never: "What you do outside tracked sessions" },
  { key: "email_summaries", label: "Email summaries", cap: "One-line summaries of emails you send", never: "Full email bodies or personal addresses" },
  { key: "calendar_events", label: "Calendar events", cap: "Meeting titles and attendees", never: "Meeting notes or personal appointments" },
  { key: "slack_messages", label: "Slack messages", cap: "Your channel messages in work context", never: "DMs, or messages with sensitive data" },
  { key: "knowledge_edits", label: "RAGNES activity", cap: "Edits, uploads, and queries you make inside RAGNES", never: "Anything matching the blocked-content filter" },
];

const HARD_BLOCK_LIST =
  "passwords, card numbers, bank details, Aadhaar/PAN/SSN, WhatsApp messages, or private keys — these are blocked at source.";

export function useConsentStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    try {
      const { data } = await api.get("/consent/status");
      setStatus(data);
    } catch { setStatus(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);
  return { status, loading, refresh };
}

export default function ConsentGate({ status, onDone }) {
  const [toggles, setToggles] = useState(() =>
    Object.fromEntries(CATEGORY_META.map((c) => [c.key, true])),
  );
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);

  if (status?.has_consent) return null;

  const submit = async (skip) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/consent", {
        capture_categories: skip ? {} : toggles,
        skip,
      });
      toast.success(skip ? "Capture is off — you can enable it anytime in Settings" : "Capture preferences saved");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center p-4" data-testid="consent-modal">
      <div className="w-full max-w-2xl card-flat p-8 max-h-[90vh] overflow-auto">
        <p className="eyebrow mb-2">Privacy first</p>
        <h1 className="font-display text-3xl leading-tight">Before we start capturing your knowledge</h1>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          RAGNES records your work activity to build your personal knowledge bot. Choose what you're comfortable sharing.
        </p>

        <div className="mt-6 divide-y divide-border/60 border border-border/60 rounded-md">
          {CATEGORY_META.map((c) => (
            <div key={c.key} className="px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{c.label}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === c.key ? null : c.key)}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  data-testid={`consent-expand-${c.key}`}
                >
                  Details <ChevronDown className={`w-3 h-3 transition-transform ${expanded === c.key ? "rotate-180" : ""}`} />
                </button>
                <button
                  type="button"
                  role="switch"
                  aria-checked={toggles[c.key]}
                  onClick={() => setToggles({ ...toggles, [c.key]: !toggles[c.key] })}
                  className={`w-10 h-6 rounded-full transition-colors relative shrink-0 ${toggles[c.key] ? "bg-emerald-500" : "bg-secondary border border-border/60"}`}
                  data-testid={`consent-toggle-${c.key}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${toggles[c.key] ? "left-[18px]" : "left-0.5"}`} />
                </button>
              </div>
              {expanded === c.key && (
                <div className="mt-2 text-xs text-muted-foreground space-y-1 border-l-2 border-border/60 pl-3">
                  <p><span className="text-foreground/80 font-medium">Captures:</span> {c.cap}</p>
                  <p><span className="text-foreground/80 font-medium">Never captures:</span> {c.never}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 flex items-start gap-2.5" data-testid="consent-hardblock-banner">
          <Lock className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="text-emerald-500 font-medium">RAGNES never captures:</span> {HARD_BLOCK_LIST}
          </p>
        </div>

        <div className="mt-6 flex gap-3 justify-end">
          <Button variant="ghost" onClick={() => submit(true)} disabled={busy} className="rounded-full" data-testid="consent-skip-btn">
            Skip for now (capture nothing)
          </Button>
          <Button onClick={() => submit(false)} disabled={busy} className="rounded-full" data-testid="consent-start-btn">
            {busy ? "Saving…" : "Start capturing (selected categories)"}
          </Button>
        </div>
      </div>
    </div>
  );
}
